import { useState, useRef } from "react";
import axios from "axios";
import { z } from "zod";
import { getData, storeData } from "@/lib/utils";
import { useDispatch } from "react-redux";
import { getMyCompany, updateAuthUser } from "@/store/authSlice";
import api from "@/store/api";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";

// Request queue to handle concurrent requests during token refresh
interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  config: any;
}

const useLogin = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Refs to track refresh state and queue
  const isRefreshing = useRef(false);
  const failedQueue = useRef<QueuedRequest[]>([]);
  const refreshPromise = useRef<Promise<any> | null>(null);

  const LoginSchema = z.object({
    email: z
      .string()
      .email({ message: "authentication.validation.invalidEmail" }),
    password: z.string().min(8, {
      message: "authentication.changePassword.validation.passwordLength",
    }),
  });

  // Process queued requests after token refresh
  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.current.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      } else {
        // Retry the original request with new token
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        resolve(api(config));
      }
    });
    failedQueue.current = [];
  };

  // Add request to queue
  const addToQueue = (config: any): Promise<any> => {
    return new Promise((resolve, reject) => {
      failedQueue.current.push({ resolve, reject, config });
    });
  };

  const refreshToken = async () => {
    let refreshToken = getData("tokens")?.refreshToken;
    if (!refreshToken) {
      // localStorage.removeItem("tokens");
      // localStorage.removeItem("userInfo");
      throw new Error("No refresh token available");
    }

    // If already refreshing, return the existing promise
    if (isRefreshing.current && refreshPromise.current) {
      return refreshPromise.current;
    }

    isRefreshing.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response: { data: any; isSuccess: boolean } = await api.post(
        "/web/auth/refresh",
        {
          refreshToken: refreshToken,
        }
      );

      setIsLoading(false);
      if (!response.data.isSuccess) {
        throw new Error("Token refresh failed");
      }

      const newTokens = response.data.data;
      storeData("tokens", newTokens);

      // Process queued requests with new token
      processQueue(null, newTokens.accessToken);

      return newTokens;
    } catch (err) {
      setIsLoading(false);
      isRefreshing.current = false;

      // Clear user data and redirect
      dispatch(updateAuthUser(null));
      dispatch(getMyCompany(null));
      localStorage.removeItem("tokens");
      localStorage.removeItem("userInfo");

      // Process queued requests with error
      processQueue(err);

      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.message || t("alerts.error.loginError"));
        console.error("Axios Error:", err.message);
      } else {
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    } finally {
      isRefreshing.current = false;
      refreshPromise.current = null;
    }
  };

  // Enhanced API interceptor to handle token refresh
  const createAuthenticatedRequest = async (config: any) => {
    try {
      return await api(config);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // If already refreshing, queue this request
        if (isRefreshing.current) {
          return addToQueue(config);
        }

        // Try to refresh token
        try {
          await refreshToken();
          // Retry the original request with new token
          const newConfig = { ...config };
          const newToken = getData("tokens")?.accessToken;
          if (newToken && newConfig.headers) {
            newConfig.headers.Authorization = `Bearer ${newToken}`;
          }
          return await api(newConfig);
        } catch (refreshError) {
          // If refresh fails, clear user data but don't reload the page
          dispatch(updateAuthUser(null));
          dispatch(getMyCompany(null));
          localStorage.removeItem("tokens");
          localStorage.removeItem("userInfo");
          throw refreshError;
        }
      }
      throw error;
    }
  };

  const login = async (
    values: z.infer<typeof LoginSchema>,
    captchaToken?: string
  ) => {
    setIsLoading(true);
    setError(null);
    // Don't reset showCaptcha here - let it stay visible if it was already shown

    try {
      const loginData = {
        ...values,
        ...(captchaToken && { captchaToken }),
      };

      const response = await api.post("/web/auth/login", loginData);
      setIsLoading(false);
      dispatch(updateAuthUser(response.data.data.user));
      return response.data;
    } catch (err) {
      setIsLoading(false);

      if (axios.isAxiosError(err)) {
        const errorMessage = err.response?.data?.message;

        // Check if captcha is required
        if (errorMessage === "Captcha verification required") {
          setShowCaptcha(true);
          setError(t("authentication.captcha.required"));
        } else {
          // Use the error message from the server or fallback to generic message
          setError(errorMessage || t("alerts.error.loginError"));
        }
        console.error("Axios Error:", err.message);
      } else {
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    }
  };

  const handleCaptchaVerify = (token: string) => {
    console.log("handleCaptchaVerify called with token:", !!token);
    setCaptchaToken(token);
    setError(null);
    // Don't reset captcha immediately, let the login process handle it
  };

  const handleCaptchaError = () => {
    console.log("handleCaptchaError called");
    setError(t("authentication.captcha.error"));
  };

  const handleCaptchaExpire = () => {
    console.log("handleCaptchaExpire called");
    setCaptchaToken(null);
    setError(t("authentication.captcha.expired"));
  };

  const resetCaptcha = () => {
    console.log("resetCaptcha called");
    setShowCaptcha(false);
    setCaptchaToken(null);
    setError(null);
  };

  const getMe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let Token = getData("tokens")?.accessToken;
      const response = await createAuthenticatedRequest({
        method: "GET",
        url: "/web/auth/me",
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });

      setIsLoading(false);
      dispatch(updateAuthUser(response.data.data.user));
      return response.data;
    } catch (err) {
      setIsLoading(false);

      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message || t("alerts.error.userDataError")
        );
        console.error("Axios Error:", err.message);
      } else {
        setError(t("alerts.error.unexpectedError"));
        console.error("Unexpected Error:", err);
      }

      throw err;
    }
  };

  return {
    login,
    refreshToken,
    getMe,
    isLoading,
    error,
    showCaptcha,
    captchaToken,
    handleCaptchaVerify,
    handleCaptchaError,
    handleCaptchaExpire,
    resetCaptcha,
    createAuthenticatedRequest,
  };
};

export default useLogin;
